import { request } from '../request';

/**
 * 查询公开需求列表
 * GET /demands
 *
 * @param params 筛选与分页参数 (包含 category_code, keyword, page, page_size)
 * @returns 公开需求列表的分页响应
 */
export function fetchPublicDemands(params?: Api.Demand.QueryDemandsParams) {
  return request<Api.Demand.DemandPageResponse>({
    url: '/demands',
    method: 'get',
    params
  });
}

/**
 * 创建需求草稿
 * POST /demands
 *
 * @param data 需求创建数据
 * @returns 创建成功的需求详情响应
 */
export function createDemandDraft(data: Api.Demand.DemandRequest) {
  return request<Api.Demand.DemandResponse>({
    url: '/demands',
    method: 'post',
    data
  });
}

/**
 * 获取公开需求详情
 * GET /demands/{id}
 *
 * @param id 需求唯一标识 ID
 * @returns 需求详情响应
 */
export function fetchPublicDemandDetail(id: string) {
  return request<Api.Demand.DemandResponse>({
    url: `/demands/${id}`,
    method: 'get'
  });
}

/**
 * 修改/更新需求草稿
 * PUT /demands/{id}
 *
 * @param id 用于修改的需求 ID
 * @param data 需求更新数据
 * @returns 更新后的需求详情响应
 */
export function updateDemandDraft(id: string, data: Api.Demand.DemandRequest) {
  return request<Api.Demand.DemandResponse>({
    url: `/demands/${id}`,
    method: 'put',
    data
  });
}

/**
 * 关闭招募中的需求
 * POST /demands/{id}/close
 *
 * @param id 需求 ID
 * @param data 关闭需求请求参数 (包含关闭原因 reason)
 * @returns 更新后的需求详情响应
 */
export function closeDemand(id: string, data?: Api.Demand.CloseDemandRequest) {
  return request<Api.Demand.DemandResponse>({
    url: `/demands/${id}/close`,
    method: 'post',
    data
  });
}

/**
 * 提交需求审核
 * POST /demands/{id}/submit
 *
 * @param id 需求 ID
 * @returns 提交审核后的需求详情响应
 */
export function submitDemandAudit(id: string) {
  return request<Api.Demand.DemandResponse>({
    url: `/demands/${id}/submit`,
    method: 'post'
  });
}

/**
 * 查询当前企业的需求列表
 * GET /my/demands
 *
 * @param params 筛选与分页参数 (包含 status, page, page_size)
 * @returns 当前企业的需求列表分页响应
 */
export function fetchMyDemands(params?: Api.Demand.QueryMyDemandsParams) {
  return request<Api.Demand.DemandPageResponse>({
    url: '/my/demands',
    method: 'get',
    params
  });
}

/**
 * 获取当前企业的需求详情
 * GET /my/demands/{id}
 *
 * @param id 需求 ID
 * @returns 当前企业的需求详情响应
 */
export function fetchMyDemandDetail(id: string) {
  return request<Api.Demand.DemandResponse>({
    url: `/my/demands/${id}`,
    method: 'get'
  });
}

/**
 * 撤回待处理报价
 * POST /demand-bids/{id}/withdraw
 *
 * @param id 报价 ID
 * @returns 报价详情响应
 */
export function withdrawDemandBid(id: string) {
  return request<Api.Demand.BidResponse>({
    url: `/demand-bids/${id}/withdraw`,
    method: 'post'
  });
}

/**
 * 对需求提交报价
 * POST /demands/{id}/bids
 *
 * @param id 需求 ID
 * @param data 报价请求体数据
 * @returns 报价详情响应
 */
export function submitDemandBid(id: string, data: Api.Demand.BidRequest) {
  return request<Api.Demand.BidResponse>({
    url: `/demands/${id}/bids`,
    method: 'post',
    data
  });
}

/**
 * 采纳需求报价
 * POST /demands/{id}/bids/{bid_id}/award
 *
 * @param id 需求 ID
 * @param bidId 报价 ID
 * @returns 报价详情响应
 */
export function awardDemandBid(id: string, bidId: string) {
  return request<Api.Demand.BidResponse>({
    url: `/demands/${id}/bids/${bidId}/award`,
    method: 'post'
  });
}

/**
 * 查询自有需求的报价时间线
 * GET /enterprise/demands/{id}/bid-timeline
 *
 * @param id 需求 ID
 * @returns 报价时间线事件列表响应
 */
export function fetchEnterpriseDemandBidTimeline(id: string) {
  return request<Api.Demand.DemandBidEventResponse[]>({
    url: `/enterprise/demands/${id}/bid-timeline`,
    method: 'get'
  });
}

/**
 * 查询自有需求的报价列表
 * GET /enterprise/demands/{id}/bids
 *
 * @param id 需求 ID
 * @param params 分页筛选参数
 * @returns 报价列表分页响应
 */
export function fetchEnterpriseDemandBids(id: string, params?: Api.Demand.QueryEnterpriseDemandBidsParams) {
  return request<Api.Demand.BidPageResponse>({
    url: `/enterprise/demands/${id}/bids`,
    method: 'get',
    params
  });
}

/**
 * 查询我的需求报价
 * GET /my/demand-bids
 *
 * @param params 筛选与分页参数
 * @returns 我的报价列表分页响应
 */
export function fetchMyDemandBids(params?: Api.Demand.QueryMyDemandBidsParams) {
  return request<Api.Demand.BidPageResponse>({
    url: '/my/demand-bids',
    method: 'get',
    params
  });
}

/**
 * 获取当前供给方提交的报价详情
 * GET /my/demand-bids/{id}
 *
 * @param id 报价 ID
 * @returns 报价详情响应
 */
export function fetchMyDemandBidDetail(id: string) {
  return request<Api.Demand.BidResponse>({
    url: `/my/demand-bids/${id}`,
    method: 'get'
  });
}

/**
 * 获取企业需求主页精选列表
 * GET /demands/featured
 *
 * @param params 筛选参数 (包含 limit)
 * @returns 企业需求主页精选列表响应
 */
export function fetchFeaturedDemands(params?: Api.Demand.QueryFeaturedDemandsParams) {
  return request<Api.Demand.DemandResponse[]>({
    url: '/demands/featured',
    method: 'get',
    params
  });
}


